import React, { useCallback, useMemo, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import { MoreVertical, Download, AlignJustify, List } from 'lucide-react';
import { useApi } from '../services/api/provider';
import { ManufacturerService } from '../services/api/domains/manufacturers/service';
import {
  fetchAllManufacturers,
  ManufacturersExportFilterParams,
} from '../services/api/domains/manufacturers/fetchAllManufacturers';
import {
  buildManufacturersExcelWorkbook,
  downloadBlob,
} from '../domains/manufacturers/adapters';
import { useTableDensity, TableDensity } from '../shared/hooks/useTableDensity';

const ICON_SIZE = 14;
const ICON_STYLE: React.CSSProperties = { marginRight: '0.5rem' };

function densityLabel(density: TableDensity): string {
  return density === 'standard'
    ? 'Switch to compact rows'
    : 'Switch to standard rows';
}

export interface ManufacturerOverflowMenuProps {
  /**
   * Current search/filter/sort params driving the grid (limit/offset are
   * ignored -- `fetchAllManufacturers` pages through every match itself).
   */
  filterParams: ManufacturersExportFilterParams;
  /** Disables the Export to Excel item -- e.g. when the current filter matches zero rows. */
  exportDisabled?: boolean;
}

/**
 * Overflow ("more actions") menu for the manufacturers grid toolbar (issue
 * #653, Sub-tasks 11.2/11.3). Consolidates two previously-standalone
 * toolbar icon buttons -- the compact-view toggle and Excel export -- and
 * upgrades export from "current page only" to every page of the current
 * filtered view (the follow-on `ManufacturerExportButton.tsx` explicitly
 * flagged when it shipped with Task 9's page-only version).
 *
 * Mirrors `ProductsRowGroupingDataGrid.tsx`'s own overflow menu shape
 * (`IconButton` + MUI `Menu`/`MenuItem`) rather than inventing a new
 * pattern -- MUI's Menu already handles keyboard navigation (Tab/Enter/
 * Arrow keys/Escape) with no extra wiring.
 */
export function ManufacturerOverflowMenu({
  filterParams,
  exportDisabled = false,
}: ManufacturerOverflowMenuProps): React.ReactElement {
  const api = useApi();
  const service = useMemo(() => new ManufacturerService(api), [api]);
  const { density, setDensity } = useTableDensity();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const handleExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const manufacturers = await fetchAllManufacturers(service, filterParams, {
        signal: controller.signal,
      });
      const blob = await buildManufacturersExcelWorkbook(manufacturers);
      const today = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `lgm-manufacturers-export-${today}.xlsx`);
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        console.error('❌ Manufacturer export failed:', err);
      }
    } finally {
      setIsExporting(false);
      abortRef.current = null;
    }
  }, [service, filterParams, isExporting]);

  const handleToggleDensity = useCallback(() => {
    setDensity(density === 'standard' ? 'compact' : 'standard');
  }, [density, setDensity]);

  return (
    <>
      <IconButton
        size="small"
        onClick={e => setAnchorEl(e.currentTarget)}
        aria-label="More actions"
        aria-haspopup="true"
        title="More actions"
        data-testid="manufacturer-overflow-menu-button">
        <MoreVertical size={16} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            handleExport();
          }}
          disabled={isExporting || exportDisabled}
          data-testid="manufacturer-overflow-export-excel">
          {isExporting ? (
            <CircularProgress
              size={ICON_SIZE}
              thickness={5}
              style={ICON_STYLE}
            />
          ) : (
            <Download size={ICON_SIZE} style={ICON_STYLE} />
          )}
          {isExporting ? 'Exporting…' : 'Export to Excel'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            handleToggleDensity();
          }}
          data-testid="manufacturer-overflow-density-toggle">
          {density === 'compact' ? (
            <List size={ICON_SIZE} style={ICON_STYLE} />
          ) : (
            <AlignJustify size={ICON_SIZE} style={ICON_STYLE} />
          )}
          {densityLabel(density)}
        </MenuItem>
      </Menu>
    </>
  );
}

export default ManufacturerOverflowMenu;
